// @ts-nocheck
// AppointmentRecord — the full appointment, rendered IN PLACE (an expanded row of
// the customer window's Appointments tab). The standalone page is gone as a
// destination: this is the one appointment screen. Reads the detail payload from
// /appointments/appointments/<id>/detail/ (CRM uuid or FieldRoutes external id):
// service, tech, when, duration, arrival window, status, FR id, customer-visible
// notes (editable — a FieldRoutes visit pushes the note back through fr-notes),
// the post-service record (products, pests, areas, sqft, photos) and commission.
//
// Feature-detected until RT's legs land: `post_service` on a FieldRoutes visit and
// `commission` on either kind are rendered only when the payload carries them.
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getAppointmentDetail, saveFieldRoutesNotes, updateAppointment, completeAppointment, cancelAppointment } from '../../api/appointmentsApi';
import { getFeatures, getIndustryKey } from '../../constants/uiRegistry';
import Button from '../ui/button';
import Badge from '../ui/badge';
import { Textarea } from '../ui/input';
import AccessSafetyCard from './AccessSafetyCard';
import './AppointmentDetail.css';

const STATUS_TONE = {
  scheduled: 'accent', confirmed: 'accent', en_route: 'accent', in_progress: 'accent',
  completed: 'success', canceled: 'neutral', cancelled: 'neutral', no_show: 'warning', missed: 'warning',
};
const PROJECT_VERTICALS = new Set(['landscaping', 'construction', 'roofing', 'remodeling', 'plumbing', 'hvac', 'electrical', 'painting']);

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};
const money = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? '' : Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' }));
const words = (s) => String(s || '').replace(/_/g, ' ');

function Fact({ k, v, mono }) {
  return (
    <div>
      <span className="apd-k">{k}</span>
      <span className={`apd-v${mono ? ' apd-mono' : ''}`}>{v == null || v === '' ? '—' : v}</span>
    </div>
  );
}

/** Loads and renders one appointment. `id` is a CRM uuid or a FieldRoutes external id. */
export default function AppointmentRecord({ id, boardStartLabel = null, onChanged, compact = true }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [extNotes, setExtNotes] = useState('');
  const [intNotes, setIntNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getAppointmentDetail(id);
      setD(r.data);
      setExtNotes(r.data?.pre_service?.external_notes || '');
      setIntNotes(r.data?.pre_service?.internal_notes || '');
      setError('');
    } catch {
      setError('Could not load this appointment.');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const isFR = d?.source === 'fieldroutes';
  const notesDirty = d ? (extNotes !== (d.pre_service?.external_notes || '') || (!isFR && intNotes !== (d.pre_service?.internal_notes || ''))) : false;

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      if (isFR) {
        await saveFieldRoutesNotes(id, extNotes);
        toast.success('Note saved to FieldRoutes');
      } else {
        await updateAppointment(id, { notes: extNotes, internal_notes: intNotes });
        toast.success('Notes saved — the tech sees these on arrival');
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="apr apr--state">Loading the visit…</div>;
  if (error) return <div className="apr apr--state apr--bad">{error} <button type="button" className="apd-retry" onClick={load}>Retry</button></div>;
  if (!d) return null;

  const pre = d.pre_service || {};
  const post = d.post_service;
  const isProject = PROJECT_VERTICALS.has(d.vertical);
  const accessSafetyEnabled = Boolean(getFeatures(getIndustryKey())?.accessSafetyCard);
  const commission = d.commission && typeof d.commission === 'object' ? d.commission : null;
  const sqft = post?.treatments?.reduce((n, t) => n + (Number(t.area_treated_sqft) || 0), 0) || post?.area_treated_sqft || null;

  const whenDisplay = (() => {
    if (isFR && boardStartLabel && pre.start) {
      const dt = new Date(pre.start);
      if (!Number.isNaN(dt.getTime())) return `${dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}, ${boardStartLabel}`;
    }
    return fmtWhen(pre.start);
  })();

  return (
    <div className={`apr${compact ? ' apr--compact' : ''}`}>
      <div className="apr__head">
        <Badge tone={STATUS_TONE[d.status] || 'neutral'}>{words(d.status)}</Badge>
        {isFR ? <span className="apr__src">FieldRoutes #{d.id}{d.notes_editable ? ' · read-only except the note' : ' · read-only'}</span> : <span className="apr__src">Booked in Abon</span>}
        {!isFR && d.editable ? (
          <span className="apr__actions">
            {d.status !== 'completed' && d.status !== 'canceled' ? <Button size="sm" variant="secondary" disabled={busy} onClick={() => act(() => completeAppointment(d.id), 'Marked complete')}>Mark complete</Button> : null}
            {d.status !== 'canceled' && d.status !== 'completed' ? <Button size="sm" variant="danger" disabled={busy} onClick={() => act(() => cancelAppointment(d.id), 'Canceled')}>Cancel visit</Button> : null}
          </span>
        ) : null}
      </div>

      {accessSafetyEnabled && d.access_safety ? <AccessSafetyCard facts={d.access_safety} /> : null}

      <div className="apd-facts apr__facts">
        <Fact k="Service" v={pre.service_type || pre.offering} />
        <Fact k={isProject ? 'Crew lead' : 'Technician'} v={pre.technician || post?.technician} />
        <Fact k="When" v={whenDisplay} />
        <Fact k="Duration" v={pre.duration_minutes ? `${pre.duration_minutes} min` : ''} />
        <Fact k="Arrival window" v={pre.arrival_window} />
        {pre.flexibility ? <Fact k="Flexibility" v={words(pre.flexibility)} /> : null}
        {!isFR ? <Fact k="Call ahead" v={pre.call_ahead ? 'Yes' : 'No'} /> : null}
        {isProject ? <Fact k="Crew size" v={pre.crew_size} /> : null}
        {isProject ? <Fact k="Est. hours" v={pre.estimated_hours != null ? `${pre.estimated_hours} h` : ''} /> : null}
        {pre.address ? <Fact k="Address" v={pre.address} /> : null}
        {commission ? <Fact k="Commission" v={[money(commission.amount), commission.rep || commission.technician, commission.status ? words(commission.status) : ''].filter(Boolean).join(' · ')} /> : null}
      </div>
      {isProject && pre.scope_of_work ? <div className="apd-note"><span className="apd-k">Scope of work</span>{pre.scope_of_work}</div> : null}
      {isProject && pre.materials_needed ? <div className="apd-note"><span className="apd-k">Materials needed</span>{pre.materials_needed}</div> : null}

      <div className="apr__notes">
        <div className="apd-notes-field">
          <div className="apd-notes-head"><span className="apd-k">Customer-visible notes</span>{isFR ? <span className="apr__hint">Saves to FieldRoutes</span> : null}</div>
          <Textarea rows={2} value={extNotes} onChange={(e) => setExtNotes(e.target.value)} placeholder="Gate code, pets, where to park — the customer can see this." />
        </div>
        {!isFR ? (
          <div className="apd-notes-field apd-notes-internal">
            <div className="apd-notes-head"><span className="apd-k">Internal (staff only)</span></div>
            <Textarea rows={2} value={intNotes} onChange={(e) => setIntNotes(e.target.value)} placeholder="What the tech should know and the customer never sees." />
          </div>
        ) : null}
        {notesDirty ? (
          <div className="apr__notes-save">
            <Button size="sm" variant="secondary" onClick={saveNotes} disabled={savingNotes}>{savingNotes ? 'Saving…' : isFR ? 'Save note to FieldRoutes' : 'Save notes'}</Button>
            <Button size="sm" variant="ghost" onClick={() => { setExtNotes(pre.external_notes || ''); setIntNotes(pre.internal_notes || ''); }} disabled={savingNotes}>Discard</Button>
          </div>
        ) : null}
      </div>

      <div className="apr__post">
        <span className="apd-card-label">Post-service record</span>
        {post ? (
          isProject ? (
            <>
              <div className="apd-facts">
                <Fact k="On site" v={post.time_on_site?.start ? `${fmtClock(post.time_on_site.start)} → ${fmtClock(post.time_on_site.end)}` : ''} />
                <Fact k="Crew size" v={post.crew_size} />
                <Fact k="Crew hours" v={post.crew_hours != null ? `${post.crew_hours} h` : ''} />
                <Fact k="Status" v={words(post.status)} />
              </div>
              {post.work_completed ? <div className="apd-note"><span className="apd-k">Work completed</span>{post.work_completed}</div> : null}
              {post.materials?.length ? (
                <div className="apd-treatments"><span className="apd-k">Materials used</span>
                  <div className="apd-table-wrap"><table className="apd-table"><thead><tr><th>Material</th><th>Qty</th></tr></thead>
                    <tbody>{post.materials.map((m, i) => <tr key={i}><td>{m.product__name || m.name || '—'}</td><td className="apd-mono">{m.qty_used ?? '—'}</td></tr>)}</tbody></table></div>
                </div>
              ) : null}
              {post.punch_list?.length ? (
                <div className="apd-punch"><span className="apd-k">Punch list</span>
                  <ul className="apd-punch-list">{post.punch_list.map((p, i) => <li key={i} className={p.completed ? 'apd-punch-done' : ''}><span className="apd-punch-mark">{p.completed ? '✓' : '○'}</span>{p.item}</li>)}</ul>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="apd-facts">
                <Fact k="On site" v={post.time_on_site?.start ? `${fmtClock(post.time_on_site.start)} → ${fmtClock(post.time_on_site.end)}` : ''} />
                <Fact k="Status" v={words(post.status)} />
                {sqft ? <Fact k="Area treated" v={`${Number(sqft).toLocaleString()} sq ft`} /> : null}
              </div>
              {post.customer_facing_summary ? <div className="apd-note"><span className="apd-k">Summary (customer)</span>{post.customer_facing_summary}</div> : null}
              {post.technician_notes ? <div className="apd-note apd-note-internal"><span className="apd-k">Tech notes (internal)</span>{post.technician_notes}</div> : null}
              {post.treated_pests?.length ? <div className="apd-chips"><span className="apd-k">Pests</span>{post.treated_pests.map((p, i) => <span key={i} className="apd-chip">{p}</span>)}</div> : null}
              {post.treated_areas?.length ? <div className="apd-chips"><span className="apd-k">Areas</span>{post.treated_areas.map((a, i) => <span key={i} className="apd-chip">{a}</span>)}</div> : null}
              {post.treatments?.length ? (
                <div className="apd-treatments"><span className="apd-k">Products applied</span>
                  <div className="apd-table-wrap"><table className="apd-table">
                    <thead><tr><th>Product</th><th>EPA reg.</th><th>Qty</th><th>Method</th><th>Target</th><th>Sq ft</th></tr></thead>
                    <tbody>{post.treatments.map((t, i) => (
                      <tr key={i}>
                        <td>{t.product_name || '—'}</td><td className="apd-mono">{t.epa_reg_number || '—'}</td>
                        <td className="apd-mono">{t.quantity != null ? `${t.quantity} ${t.unit || ''}`.trim() : '—'}</td>
                        <td>{words(t.application_method) || '—'}</td><td>{t.target_issue || '—'}</td><td className="apd-mono">{t.area_treated_sqft ?? '—'}</td>
                      </tr>))}</tbody>
                  </table></div>
                </div>
              ) : null}
            </>
          )
        ) : (
          <div className="apd-muted">
            {isFR
              ? 'Not on the visit record from FieldRoutes yet.'
              : isProject
                ? 'Not serviced yet. The crew logs work completed, materials, crew hours, punch list and photos from the field — it lands here.'
                : 'Not serviced yet. The tech records products, treated pests and areas, sq ft and photos from the field — it lands here.'}
          </div>
        )}
        {post?.photos?.length ? (
          <div className="apd-photos"><span className="apd-k">Photos</span>
            <div className="apd-photo-grid">{post.photos.map((p, i) => <div key={i} className="apd-photo" style={p.url ? { backgroundImage: `url(${p.url})` } : {}} title={p.label || ''} />)}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
